import { useApolloClient } from "@apollo/client";
import { message } from "antd";
import { useCallback, useState } from "react";
import {
  DESIGN_SCOPE_IMPORT_TEMPLATE_FILENAME,
  downloadDesignScopeImportTemplate,
  getFilenameFromContentDisposition,
  importDesignScopeCatalog,
  listDesignScopeCatalogs,
  deleteDesignScopeCatalog,
  triggerBlobFileDownload,
  type DesignScopeCatalog,
  type ImportDesignScopeCatalogParams,
  type ImportDesignScopeCatalogResponse
} from "./apis";
import {
  DESIGN_SCOPE_ACTION_ERROR,
  DESIGN_SCOPE_LIST_DELETED,
  designScopeActionErrorMessage
} from "./design-scope-toast-messages";

/**
 * Network actions for Design Scope List (subscription level).
 */
export function useBootstrapDesignScopeApis() {
  const apolloClient = useApolloClient();
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importingCatalog, setImportingCatalog] = useState(false);
  const [catalogs, setCatalogs] = useState<DesignScopeCatalog[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const downloadImportTemplate = useCallback(async () => {
    if (downloadingTemplate) return { success: false as const };
    setDownloadingTemplate(true);
    try {
      const response = await downloadDesignScopeImportTemplate();
      if (!response.success || !(response.data instanceof Blob)) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        return { success: false as const };
      }

      const contentDisposition = response.response?.headers?.[
        "content-disposition"
      ] as string | undefined;
      const fileName = getFilenameFromContentDisposition(
        contentDisposition,
        DESIGN_SCOPE_IMPORT_TEMPLATE_FILENAME
      );

      triggerBlobFileDownload(response.data, fileName);
      return { success: true as const };
    } catch {
      message.error(DESIGN_SCOPE_ACTION_ERROR);
      return { success: false as const };
    } finally {
      setDownloadingTemplate(false);
    }
  }, [downloadingTemplate]);

  const importCatalog = useCallback(
    async (params: ImportDesignScopeCatalogParams) => {
      if (importingCatalog) return { success: false as const };
      setImportingCatalog(true);
      try {
        const response = await importDesignScopeCatalog(params);
        if (!response.success) {
          message.error(designScopeActionErrorMessage(response.errorMsg));
          return { success: false as const };
        }

        const data = response.data as
          | ImportDesignScopeCatalogResponse
          | undefined;
        if (!data?.catalogId) {
          message.error(DESIGN_SCOPE_ACTION_ERROR);
          return { success: false as const };
        }

        return { success: true as const, data };
      } catch {
        message.error(DESIGN_SCOPE_ACTION_ERROR);
        return { success: false as const };
      } finally {
        setImportingCatalog(false);
      }
    },
    [importingCatalog]
  );

  const loadCatalogs = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      if (!silent) setLoadingCatalogs(true);
      try {
        const response = await listDesignScopeCatalogs(apolloClient);
        if (!response.success) {
          message.error(designScopeActionErrorMessage(response.errorMsg));
          setCatalogs([]);
          return { success: false as const };
        }

        const rows = Array.isArray(response.data)
          ? (response.data as DesignScopeCatalog[])
          : [];
        setCatalogs(rows);
        return { success: true as const };
      } catch {
        message.error(DESIGN_SCOPE_ACTION_ERROR);
        setCatalogs([]);
        return { success: false as const };
      } finally {
        // Always clear: a silent refresh may have superseded one that showed the spinner.
        setLoadingCatalogs(false);
      }
    },
    [apolloClient]
  );

  const updateCatalog = useCallback(
    (
      catalogId: string,
      patch: { name: string; description: string | null }
    ) => {
      setCatalogs((current) =>
        current.map((catalog) =>
          catalog.id === catalogId ? { ...catalog, ...patch } : catalog
        )
      );
    },
    []
  );

  const deleteCatalog = useCallback(
    async (catalogId: string) => {
      const response = await deleteDesignScopeCatalog(apolloClient, catalogId);
      if (!response.success) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        return false;
      }
      message.success(DESIGN_SCOPE_LIST_DELETED);
      setCatalogs((current) =>
        current.filter((catalog) => catalog.id !== catalogId)
      );
      return true;
    },
    [apolloClient]
  );

  return {
    downloadImportTemplate,
    downloadingTemplate,
    importCatalog,
    importingCatalog,
    catalogs,
    loadingCatalogs,
    loadCatalogs,
    updateCatalog,
    deleteCatalog
  };
}
